import { useState, useEffect } from 'react';
import { Modal } from './Modal';
import { FormInput } from './FormFields';
import { useCollaborationStore } from '../store/collaborationStore';
import type { PermissionLevel } from '@poi-planner/shared';

function buildInviteUrl(token: string): string {
  return `${window.location.origin}/invite/${token}`;
}

interface ShareTripModalProps {
  isOpen: boolean;
  onClose: () => void;
  tripId: string;
  tripName: string;
  isOwner: boolean;
}

function formatExpiryTime(expiresAt: string): string {
  const now = new Date();
  const expiry = new Date(expiresAt);
  const diffMs = expiry.getTime() - now.getTime();
  const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays <= 0) return 'Expired';
  if (diffDays === 1) return 'Expires tomorrow';
  if (diffDays < 7) return `Expires in ${diffDays} days`;
  return `Expires ${expiry.toLocaleDateString()}`;
}

export function ShareTripModal({ isOpen, onClose, tripId, tripName, isOwner }: ShareTripModalProps) {
  const {
    shares,
    inviteLinks,
    loading,
    loadTripShares,
    loadInviteLinks,
    createInviteLink,
    cancelInviteLink,
    updatePermission,
    removeShare,
  } = useCollaborationStore();

  const [permission, setPermission] = useState<PermissionLevel>('editor');
  const [label, setLabel] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [createdLink, setCreatedLink] = useState<string | null>(null);
  const [copiedLinkId, setCopiedLinkId] = useState<string | null>(null);

  // Filter to only show pending links
  const pendingLinks = inviteLinks.filter((link) => link.status === 'pending');

  // Load data when modal opens
  useEffect(() => {
    if (isOpen && tripId) {
      loadTripShares(tripId);
      loadInviteLinks(tripId);
      setPermission('editor');
      setLabel('');
      setError(null);
      setCreatedLink(null);
      setCopiedLinkId(null);
    }
  }, [isOpen, tripId, loadTripShares, loadInviteLinks]);

  const handleCreateLink = async () => {
    setError(null);
    setIsCreating(true);
    setCreatedLink(null);

    try {
      const url = await createInviteLink(tripId, permission, label.trim() || undefined);
      setCreatedLink(url);
      setLabel('');
    } catch (err: any) {
      setError(err.message || 'Failed to create invite link');
    } finally {
      setIsCreating(false);
    }
  };

  const handleCopyLink = async (linkUrl: string, linkId?: string) => {
    try {
      await navigator.clipboard.writeText(linkUrl);
      if (linkId) {
        setCopiedLinkId(linkId);
        setTimeout(() => setCopiedLinkId(null), 2000);
      }
    } catch (err) {
      console.error('Failed to copy link:', err);
    }
  };

  const handleCancelLink = async (linkId: string) => {
    try {
      await cancelInviteLink(linkId);
    } catch (err) {
      console.error('Failed to cancel link:', err);
    }
  };

  const handleUpdatePermission = async (shareId: string, newPermission: PermissionLevel) => {
    try {
      await updatePermission(shareId, newPermission);
    } catch (err) {
      console.error('Failed to update permission:', err);
    }
  };

  const handleRemoveShare = async (shareId: string) => {
    try {
      await removeShare(shareId);
    } catch (err) {
      console.error('Failed to remove share:', err);
    }
  };

  if (!isOwner) {
    return (
      <Modal open={isOpen} onClose={onClose} title="Share Trip" icon="share" width="560px">
        <div className="text-center py-8 text-text-secondary">
          Only the trip owner can manage sharing settings.
        </div>
      </Modal>
    );
  }

  return (
    <Modal open={isOpen} onClose={onClose} title={`Share "${tripName}"`} icon="share" width="600px">
      <div className="share-modal-content">
        {/* Create Invite Link Section */}
        <div className="invite-section">
          <div className="section-header">
            <div className="section-header-text">
              <h4 className="section-title">Create Invite Link</h4>
              <p className="section-subtitle">Generate a shareable link for anyone to join</p>
            </div>
            <span className="material-symbols-outlined section-icon">link</span>
          </div>

          <div className="invite-form">
            <div className="form-row">
              {/* Permission Select */}
              <div className="form-group flex-1">
                <label className="form-label">Permission</label>
                <select
                  value={permission}
                  onChange={(e) => setPermission(e.target.value as PermissionLevel)}
                  className="select-base"
                >
                  <option value="editor">Editor - Can view and edit</option>
                  <option value="viewer">Viewer - Can only view</option>
                </select>
              </div>

              {/* Label Input */}
              <div className="form-group flex-1">
                <label className="form-label">Label (optional)</label>
                <FormInput
                  value={label}
                  onChange={setLabel}
                  placeholder="e.g., For mom, Work team"
                />
              </div>
            </div>

            {/* Error Message */}
            {error && (
              <div className="error-message">
                <span className="material-symbols-outlined">error</span>
                <span>{error}</span>
              </div>
            )}

            {/* Created Link Display */}
            {createdLink && (
              <div className="created-link-display">
                <div className="created-link-header">
                  <span className="material-symbols-outlined success-icon">check_circle</span>
                  <span>Link created! Share it with your collaborator:</span>
                </div>
                <div className="created-link-url">
                  <input
                    type="text"
                    value={createdLink}
                    readOnly
                    className="link-input"
                    onClick={(e) => (e.target as HTMLInputElement).select()}
                  />
                  <button
                    onClick={() => handleCopyLink(createdLink)}
                    className="btn-copy-created"
                  >
                    <span className="material-symbols-outlined">content_copy</span>
                    Copy
                  </button>
                </div>
              </div>
            )}

            {/* Create Button */}
            <button
              onClick={handleCreateLink}
              disabled={isCreating}
              className="btn-create"
            >
              {isCreating ? (
                <>
                  <span className="loading-spinner"></span>
                  <span>Creating...</span>
                </>
              ) : (
                <>
                  <span className="material-symbols-outlined">add_link</span>
                  <span>Create Link</span>
                </>
              )}
            </button>
          </div>
        </div>

        {/* Active Invite Links Section */}
        {pendingLinks.length > 0 && (
          <div className="links-section">
            <div className="section-header">
              <div className="section-header-text">
                <h4 className="section-title">Active Invite Links</h4>
              </div>
              <span className="link-count">{pendingLinks.length}</span>
            </div>

            <div className="links-list">
              {pendingLinks.map((link, index) => {
                const linkUrl = buildInviteUrl(link.token);
                const isCopied = copiedLinkId === link.id;

                return (
                  <div
                    key={link.id}
                    className="link-item"
                    style={{ animationDelay: `${index * 50}ms` }}
                  >
                    <div className="link-info">
                      <div className="link-icon">
                        <span className="material-symbols-outlined">link</span>
                      </div>
                      <div className="link-details">
                        <div className="link-name">
                          {link.label || `${link.permissionLevel === 'editor' ? 'Editor' : 'Viewer'} link`}
                        </div>
                        <div className="link-meta">
                          <span className={`permission-badge ${link.permissionLevel}`}>
                            {link.permissionLevel}
                          </span>
                          <span className="expiry-text">{formatExpiryTime(link.expiresAt)}</span>
                        </div>
                      </div>
                    </div>

                    <div className="link-actions">
                      <button
                        onClick={() => handleCopyLink(linkUrl, link.id)}
                        className={`btn-copy ${isCopied ? 'copied' : ''}`}
                        title="Copy link"
                      >
                        <span className="material-symbols-outlined">
                          {isCopied ? 'check' : 'content_copy'}
                        </span>
                        {isCopied ? 'Copied!' : 'Copy'}
                      </button>

                      <button
                        onClick={() => handleCancelLink(link.id)}
                        className="btn-cancel"
                        title="Cancel link"
                      >
                        <span className="material-symbols-outlined">close</span>
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Collaborators List */}
        <div className="collaborators-section">
          <div className="section-header">
            <div className="section-header-text">
              <h4 className="section-title">Collaborators</h4>
            </div>
            <span className="collaborator-count">{shares.length}</span>
          </div>

          {loading ? (
            <div className="empty-state">
              <span className="loading-spinner large"></span>
              <p>Loading collaborators...</p>
            </div>
          ) : shares.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon">
                <span className="material-symbols-outlined">group</span>
              </div>
              <p className="empty-title">No collaborators yet</p>
              <p className="empty-subtitle">Create an invite link above to share this trip</p>
            </div>
          ) : (
            <div className="collaborators-list">
              {shares.map((share, index) => (
                <div
                  key={share.id}
                  className="collaborator-item"
                  style={{ animationDelay: `${index * 50}ms` }}
                >
                  <div className="collaborator-info">
                    <div className="collaborator-avatar">
                      <span className="material-symbols-outlined">person</span>
                    </div>
                    <div className="collaborator-details">
                      <div className="collaborator-email">{share.sharedWithUserId}</div>
                      <div className="collaborator-meta">
                        Added {new Date(share.createdAt).toLocaleDateString()}
                      </div>
                    </div>
                  </div>

                  <div className="collaborator-actions">
                    <select
                      value={share.permissionLevel}
                      onChange={(e) =>
                        handleUpdatePermission(share.id, e.target.value as PermissionLevel)
                      }
                      className="permission-select"
                    >
                      <option value="editor">Editor</option>
                      <option value="viewer">Viewer</option>
                    </select>

                    <button
                      onClick={() => handleRemoveShare(share.id)}
                      className="btn-remove"
                      title="Remove access"
                    >
                      <span className="material-symbols-outlined">close</span>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <style>{`
        /* Share Modal Styling */
        .share-modal-content {
          display: flex;
          flex-direction: column;
          gap: 28px;
        }

        /* Section Headers */
        .section-header {
          margin-bottom: 16px;
          padding-bottom: 12px;
          border-bottom: 2px solid #f97316;
          display: flex;
          align-items: center;
          justify-content: space-between;
        }

        .section-header-text {
          display: flex;
          flex-direction: column;
          gap: 2px;
        }

        .section-title {
          font-size: 13px;
          font-weight: 700;
          color: #111418;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          margin: 0;
        }

        .section-subtitle {
          font-size: 12px;
          color: #617589;
          margin: 0;
        }

        .section-icon {
          font-size: 20px;
          color: #f97316;
        }

        .collaborator-count,
        .link-count {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          min-width: 24px;
          height: 24px;
          padding: 0 8px;
          background: #fff7ed;
          color: #f97316;
          border-radius: 12px;
          font-size: 12px;
          font-weight: 700;
        }

        /* Form Styling */
        .invite-form {
          display: flex;
          flex-direction: column;
          gap: 16px;
        }

        .form-row {
          display: flex;
          gap: 12px;
        }

        .form-group {
          display: flex;
          flex-direction: column;
          gap: 6px;
        }

        .form-group.flex-1 {
          flex: 1;
        }

        .form-label {
          font-size: 11px;
          font-weight: 600;
          color: #617589;
          text-transform: uppercase;
          letter-spacing: 0.3px;
        }

        /* Created Link Display */
        .created-link-display {
          background: linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%);
          border: 1px solid #a7f3d0;
          border-radius: 10px;
          padding: 14px;
          animation: slideDown 0.25s ease-out;
        }

        .created-link-header {
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 13px;
          font-weight: 600;
          color: #065f46;
          margin-bottom: 10px;
        }

        .success-icon {
          font-size: 18px;
          color: #10b981;
        }

        .created-link-url {
          display: flex;
          gap: 8px;
        }

        .link-input {
          flex: 1;
          padding: 10px 12px;
          background: white;
          border: 1px solid #a7f3d0;
          border-radius: 6px;
          font-size: 12px;
          font-family: 'SF Mono', 'Monaco', 'Inconsolata', monospace;
          color: #111418;
        }

        .link-input:focus {
          outline: none;
          border-color: #10b981;
        }

        .btn-copy-created {
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 10px 16px;
          background: #10b981;
          color: white;
          border: none;
          border-radius: 6px;
          font-size: 12px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.15s ease;
          white-space: nowrap;
        }

        .btn-copy-created:hover {
          background: #059669;
        }

        .btn-copy-created .material-symbols-outlined {
          font-size: 16px;
        }

        /* Error Message */
        .error-message {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 10px 12px;
          background: #fef3f2;
          border: 1px solid #fecaca;
          border-radius: 6px;
          color: #991b1b;
          font-size: 13px;
          font-weight: 500;
        }

        .error-message .material-symbols-outlined {
          font-size: 18px;
        }

        /* Create Button */
        .btn-create {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          padding: 12px 20px;
          background: linear-gradient(135deg, #f97316 0%, #ea580c 100%);
          color: white;
          border: none;
          border-radius: 8px;
          font-size: 14px;
          font-weight: 700;
          font-family: 'Plus Jakarta Sans', sans-serif;
          cursor: pointer;
          transition: all 0.2s ease;
          box-shadow: 0 2px 8px rgba(249, 115, 22, 0.2);
        }

        .btn-create:hover:not(:disabled) {
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(249, 115, 22, 0.3);
        }

        .btn-create:active:not(:disabled) {
          transform: translateY(0);
        }

        .btn-create:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .btn-create .material-symbols-outlined {
          font-size: 18px;
        }

        /* Links List */
        .links-list {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .link-item {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 12px 14px;
          background: #fafbfc;
          border: 1px solid #e5e7eb;
          border-radius: 8px;
          transition: all 0.2s ease;
          animation: fadeSlideIn 0.3s ease-out backwards;
        }

        .link-item:hover {
          border-color: #f97316;
          background: white;
          box-shadow: 0 2px 8px rgba(249, 115, 22, 0.06);
        }

        .link-info {
          display: flex;
          align-items: center;
          gap: 12px;
          flex: 1;
          min-width: 0;
        }

        .link-icon {
          width: 36px;
          height: 36px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: linear-gradient(135deg, #fff7ed 0%, #ffedd5 100%);
          border-radius: 8px;
          flex-shrink: 0;
        }

        .link-icon .material-symbols-outlined {
          font-size: 18px;
          color: #f97316;
        }

        .link-details {
          display: flex;
          flex-direction: column;
          gap: 4px;
          min-width: 0;
        }

        .link-name {
          font-size: 13px;
          font-weight: 600;
          color: #111418;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .link-meta {
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .permission-badge {
          display: inline-flex;
          align-items: center;
          padding: 2px 8px;
          border-radius: 4px;
          font-size: 10px;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.3px;
        }

        .permission-badge.editor {
          background: #dbeafe;
          color: #1d4ed8;
        }

        .permission-badge.viewer {
          background: #f3e8ff;
          color: #7c3aed;
        }

        .expiry-text {
          font-size: 11px;
          color: #8b949e;
        }

        .link-actions {
          display: flex;
          align-items: center;
          gap: 6px;
        }

        .btn-copy {
          display: flex;
          align-items: center;
          gap: 4px;
          padding: 6px 12px;
          background: white;
          border: 1px solid #e5e7eb;
          border-radius: 6px;
          font-size: 11px;
          font-weight: 600;
          color: #617589;
          cursor: pointer;
          transition: all 0.15s ease;
        }

        .btn-copy:hover {
          background: #f0f2f4;
          border-color: #617589;
          color: #111418;
        }

        .btn-copy.copied {
          background: #ecfdf5;
          border-color: #a7f3d0;
          color: #059669;
        }

        .btn-copy .material-symbols-outlined {
          font-size: 14px;
        }

        .btn-cancel {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 28px;
          height: 28px;
          background: transparent;
          border: 1px solid #e5e7eb;
          border-radius: 6px;
          color: #8b949e;
          cursor: pointer;
          transition: all 0.15s ease;
        }

        .btn-cancel:hover {
          background: #fef3f2;
          border-color: #fecaca;
          color: #ef4444;
        }

        .btn-cancel .material-symbols-outlined {
          font-size: 16px;
        }

        /* Loading Spinner */
        .loading-spinner {
          display: inline-block;
          width: 14px;
          height: 14px;
          border: 2px solid rgba(255, 255, 255, 0.3);
          border-top-color: white;
          border-radius: 50%;
          animation: spin 0.6s linear infinite;
        }

        .loading-spinner.large {
          width: 24px;
          height: 24px;
          border-width: 3px;
          border-color: rgba(97, 117, 137, 0.2);
          border-top-color: #617589;
        }

        @keyframes spin {
          to { transform: rotate(360deg); }
        }

        @keyframes slideDown {
          from {
            opacity: 0;
            transform: translateY(-8px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes fadeSlideIn {
          from {
            opacity: 0;
            transform: translateX(-8px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }

        /* Empty State */
        .empty-state {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 40px 24px;
          text-align: center;
        }

        .empty-icon {
          width: 56px;
          height: 56px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: linear-gradient(135deg, #fff7ed 0%, #ffedd5 100%);
          border-radius: 14px;
          margin-bottom: 14px;
        }

        .empty-icon .material-symbols-outlined {
          font-size: 28px;
          color: #f97316;
        }

        .empty-title {
          font-size: 14px;
          font-weight: 700;
          color: #111418;
          margin: 0 0 4px 0;
        }

        .empty-subtitle {
          font-size: 13px;
          color: #617589;
          margin: 0;
        }

        /* Collaborators List */
        .collaborators-list {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .collaborator-item {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 12px 14px;
          background: white;
          border: 1px solid #e5e7eb;
          border-radius: 8px;
          transition: all 0.2s ease;
          animation: fadeSlideIn 0.3s ease-out backwards;
        }

        .collaborator-item:hover {
          border-color: #f97316;
          box-shadow: 0 2px 8px rgba(249, 115, 22, 0.08);
        }

        .collaborator-info {
          display: flex;
          align-items: center;
          gap: 12px;
          flex: 1;
          min-width: 0;
        }

        .collaborator-avatar {
          width: 36px;
          height: 36px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: linear-gradient(135deg, #fff7ed 0%, #ffedd5 100%);
          border-radius: 50%;
          flex-shrink: 0;
        }

        .collaborator-avatar .material-symbols-outlined {
          font-size: 18px;
          color: #f97316;
        }

        .collaborator-details {
          display: flex;
          flex-direction: column;
          gap: 2px;
          min-width: 0;
        }

        .collaborator-email {
          font-size: 13px;
          font-weight: 600;
          color: #111418;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .collaborator-meta {
          font-size: 11px;
          color: #8b949e;
        }

        .collaborator-actions {
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .permission-select {
          padding: 6px 28px 6px 10px;
          background: #f0f2f4;
          border: 1px solid transparent;
          border-radius: 6px;
          font-size: 11px;
          font-weight: 600;
          color: #111418;
          cursor: pointer;
          transition: all 0.15s ease;
          appearance: none;
          background-image: url("data:image/svg+xml,%3Csvg width='10' height='6' viewBox='0 0 10 6' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1 1L5 5L9 1' stroke='%23617589' stroke-width='1.5' stroke-linecap='round'/%3E%3C/svg%3E");
          background-repeat: no-repeat;
          background-position: right 8px center;
        }

        .permission-select:hover {
          background-color: #e5e7eb;
          border-color: #617589;
        }

        .permission-select:focus {
          outline: none;
          border-color: #f97316;
        }

        .btn-remove {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 28px;
          height: 28px;
          background: transparent;
          border: 1px solid #e5e7eb;
          border-radius: 6px;
          color: #8b949e;
          cursor: pointer;
          transition: all 0.15s ease;
        }

        .btn-remove:hover {
          background: #fef3f2;
          border-color: #fecaca;
          color: #ef4444;
        }

        .btn-remove .material-symbols-outlined {
          font-size: 16px;
        }
      `}</style>
    </Modal>
  );
}
